import classNames from 'classnames'
import * as d3 from 'd3'
import { Component, createRef } from 'react'

import { isFirefoxDesktop } from '../../shared/isMobile'
import { retinafy } from '../../shared/retinafy'

const X_AXIS_TICK_OFFSET = 1
const xCache: Record<string, d3.ScaleLinear<number, number>> = {}

interface CanvasSparklineProps {
  className?: string
  data: number[]
  dateChanges: Record<string, string>
  width: number
  height: number
  changeDomain: number
  change: number
  changeOpacity?: boolean
  shadow?: boolean
  lineColor?: string
  textColor?: string
  hasStickyLabels?: boolean
}

class CanvasSparkline extends Component<CanvasSparklineProps> {
  static defaultProps = {
    changeOpacity: true,
  }

  private readonly canvas = createRef<HTMLCanvasElement>()
  declare private canvasContext: CanvasRenderingContext2D

  shouldComponentUpdate(nextProps: CanvasSparklineProps) {
    // eslint-disable-next-line eqeqeq
    return nextProps.data != this.props.data || nextProps.width !== this.props.width
  }

  componentDidMount() {
    this.canvasContext = this.canvas.current!.getContext(
      '2d',
      isFirefoxDesktop() ? { willReadFrequently: true } : undefined
    )!
    this._renderCanvas()
  }

  componentDidUpdate() {
    this._renderCanvas()
  }

  render() {
    const { width, height, className } = this.props
    return <canvas className={classNames('sparkline', className)} width={width} height={height} ref={this.canvas} />
  }

  private _renderXAxisLabels = (x: d3.ScaleLinear<number, number>) => {
    const { dateChanges, width, height, hasStickyLabels } = this.props
    const keys = Object.keys(dateChanges)
    keys.forEach((key, index) => {
      const text = dateChanges[key]
      const metrics = this.canvasContext.measureText(text)
      const textWidth = metrics.width
      let xPos = x(Number(key))

      if (index === keys.length - 1) {
        const rightBound = ~~(width - textWidth / 2)

        if (hasStickyLabels) {
          xPos = Math.min(rightBound - X_AXIS_TICK_OFFSET, xPos)
        } else if (xPos > rightBound) {
          return
        }
      } else if (index === 0) {
        const leftBound = ~~(textWidth / 2)

        if (hasStickyLabels) {
          xPos = Math.max(leftBound + X_AXIS_TICK_OFFSET, xPos)
        } else if (xPos < leftBound) {
          return
        }
      }

      this.canvasContext.fillText(text, xPos, height - 1)
    })
  }

  private _renderCanvas = () => {
    const { data, width, height, changeOpacity, shadow, lineColor, textColor, changeDomain, change } = this.props

    retinafy(this.canvas.current, this.canvasContext, width, height)

    // TODO check ci sa zobrazuje posledny den
    const xCacheKey = `${width}|${data.length}`
    let x = xCache[xCacheKey]
    if (!x) {
      x = d3
        .scaleLinear()
        .range([-1, width + 1])
        .domain([0, data.length])
      xCache[xCacheKey] = x
    }

    const y = d3
      .scaleLinear()
      .range([height - 8, 0])
      .domain(d3.extent(data, (d) => d) as [number, number])

    const opacityChange = Math.min(Math.abs(change), changeDomain)
    const textOpacity = changeOpacity ? (0.5 + (0.2 / changeDomain) * opacityChange).toFixed(3) : 1

    this.canvasContext.clearRect(0, 0, width, height)

    if (shadow) {
      this.canvasContext.translate(0.5, 0.5)

      this.canvasContext.strokeStyle = 'rgba(34, 38, 47, 0.8)'
      this.canvasContext.beginPath()
      for (let i = 0; i < data.length; i++) {
        this.canvasContext.lineTo(x(i), y(data[i]))
      }
      this.canvasContext.stroke()

      this.canvasContext.fillStyle = 'rgba(34, 38, 47, 0.8)'
      this.canvasContext.textAlign = 'center'
      this.canvasContext.font = '7px Lato'
      this._renderXAxisLabels(x)

      this.canvasContext.translate(-0.5, -0.5)
    }

    this.canvasContext.strokeStyle = lineColor ?? `rgba(255, 255, 255, ${textOpacity})`
    this.canvasContext.beginPath()
    for (let i = 0; i < data.length; i++) {
      this.canvasContext.lineTo(x(i), y(data[i]))
    }
    this.canvasContext.stroke()

    this.canvasContext.fillStyle = textColor ?? `rgba(255, 255, 255, ${textOpacity})`
    this.canvasContext.textAlign = 'center'
    this.canvasContext.font = '7px Lato'
    this._renderXAxisLabels(x)
  }
}

export default CanvasSparkline
